import { useMemo, useState } from 'react';
import MediaLightbox, {
  MediaLightboxProps,
} from '@/components/molecules/media-lightbox/media-lightbox';
import ItemGrid from '@/components/utilities/item-grid/item-grid';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import MediaGridItem from './media-grid-item/media-grid-item';

export interface MediaGridProps {
  /** The class name to apply to the component */
  className?: string;
  /** The items to display in the grid */
  items: Content.Properties.ImageAndVideoGridItem[];
  /** Indicates whether the image should be loaded immediately */
  priority?: boolean;
  /** The size of the lightbox */
  size?: MediaLightboxProps['size'];
}

const MAX_ITEMS_SIZE_LARGE = 5;
const MAX_ITEMS_SIZE_MEDIUM = 4;

const MediaGrid = ({
  className,
  items = [],
  priority = false,
  size = 'large',
}: MediaGridProps) => {
  const [activeImageIndex, setActiveImageIndex] = useState<number>(-1);
  const { t } = useI18n();

  const isMedium = useWindowSize('tablet', 'laptop');

  const gridItemsRender = useMemo(() => {
    const sizeToCompare = isMedium
      ? MAX_ITEMS_SIZE_MEDIUM
      : MAX_ITEMS_SIZE_LARGE;

    const hasManyItems = items.length > sizeToCompare;
    const overflowAmount = hasManyItems
      ? items.length - sizeToCompare
      : undefined;

    const backdropText =
      overflowAmount !== undefined
        ? overflowAmount > 1
          ? `+${overflowAmount} ${t('items.common.words.image_other')}`
          : `+${overflowAmount} ${t('items.common.words.image_one')}`
        : undefined;

    const handleItemSelect = (id: string) => {
      const activeIndex = items.findIndex(item => item.id === id);

      setActiveImageIndex(activeIndex);
    };

    return items.slice(0, sizeToCompare).map((item, index) => {
      const isLastItem = index === sizeToCompare - 1;

      return (
        <MediaGridItem
          {...item}
          key={item.id}
          backdropText={isLastItem ? backdropText : undefined}
          onClick={handleItemSelect}
          priority={priority}
        />
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, isMedium, priority]);

  return (
    <div className={className}>
      <ItemGrid>{gridItemsRender}</ItemGrid>

      <MediaLightbox
        isOpen={activeImageIndex !== -1}
        items={items.map(item => ({
          media: item,
        }))}
        onClose={() => setActiveImageIndex(-1)}
        size={size}
        startIndex={activeImageIndex}
      />
    </div>
  );
};

MediaGrid.displayName = 'MediaGrid';

export default MediaGrid;
